import { isMobile as isMobileReactDeviceDetect, isTablet as isTabletReactDeviceDetect } from "react-device-detect";

export function detectDeviceType(): "Mobile" | "Tablet" | "Desktop" {
  // Device dimensions
  const screenWidth = window.screen.width;
  const screenHeight = window.screen.height;

  // For tablets:
  // 1. Either dimension should be in tablet range (700-1400px)
  // 2. The smaller dimension must be at least 700px (to exclude phones)
  // 3. The larger dimension must be less than 1400px (to exclude desktops)
  const minDimension = Math.min(screenWidth, screenHeight);
  const maxDimension = Math.max(screenWidth, screenHeight);

  // iPad Mini: 768x1024
  // iPad: 810x1080
  // iPad Pro 11": 834x1194
  // iPad Pro 12.9": 1024x1366
  // Adding some buffer for different resolutions and orientations
  const isTabletSize =
    minDimension >= 700 && // Minimum tablet dimension
    maxDimension <= 1400 && // Maximum tablet dimension
    screenWidth <= 1400; // Additional check for desktop screens

  if (isTabletSize || isTabletReactDeviceDetect) {
    return "Tablet";
  }

  // if they are running the mobile app or the device is small, and it is not a tablet, return mobile
  if (isMobileReactDeviceDetect || screenWidth < 700) {
    return "Mobile";
  }

  return "Desktop";
}

export const isIOS = () => {
  // Check if running in browser environment
  if (typeof window === "undefined") return false;

  const globalWindow: any = typeof window !== "undefined" ? window : {};

  // Use platform detection when available
  if ("userAgentData" in navigator && (navigator as any).userAgentData?.platform) {
    return ["iPhone", "iPad", "iPod"].includes((navigator as any).userAgentData.platform);
  }

  // Fallback to userAgent for older browsers
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) &&
    !globalWindow.MSStream &&
    "maxTouchPoints" in navigator &&
    navigator.maxTouchPoints > 0
  );
};

export const isAndroid = () => {
  // Check if running in browser environment
  if (typeof window === "undefined") return false;

  const globalWindow: any = typeof window !== "undefined" ? window : {};

  // Use platform detection when available
  if ("userAgentData" in navigator && (navigator as any).userAgentData?.platform) {
    return (navigator as any).userAgentData.platform === "Android";
  }

  // Fallback to userAgent for older browsers
  return (
    /Android/.test(navigator.userAgent) &&
    !globalWindow.MSStream &&
    "maxTouchPoints" in navigator &&
    navigator.maxTouchPoints > 0
  );
};

/**
 * Whether a device of this type is used by touch rather than with a mouse or trackpad. "Tablet" is a guess from the
 * screen's size that also takes in small laptop screens (1366x768, 1280x800), so a tablet whose main pointer is fine
 * and can hover counts as a computer.
 */
export function isDeviceTypeUsedByTouch(
  deviceType: "Mobile" | "Tablet" | "Desktop",
  hasFinePointerThatCanHover: boolean
): boolean {
  if (deviceType === "Mobile") return true;
  if (deviceType === "Tablet") return !hasFinePointerThatCanHover;
  return false;
}

// Worked out once, when the module loads. The marketing pages also load it at build time, in Node, where there is no
// window: there the answers are a computer's, and nothing rendered at build time reads them
const isRunningInABrowser = typeof window !== "undefined";
const currentDeviceType = isRunningInABrowser ? detectDeviceType() : "Desktop";

export const isMobile: boolean = currentDeviceType === "Mobile";
export const isTablet: boolean = currentDeviceType === "Tablet";
export const isDesktop: boolean = currentDeviceType === "Desktop";
// Jungle's app is used by touch too, which its pages learn later (isRunningAsMobileApp)
export const isTouchFirstDevice: boolean = isDeviceTypeUsedByTouch(
  currentDeviceType,
  isRunningInABrowser &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(hover: hover) and (pointer: fine)").matches
);

// console.log("isMobile", isMobile);
// console.log("isTablet", isTablet);
// console.log("isDesktop", isDesktop);
